import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import { motion } from "framer-motion";
import * as Yup from "yup";
import moment from "moment";
import { getVendors } from "../../../store/AsyncMethods/VendorMethod";
import VendorSalesReportsTable from "./VendorSalesReportsTable";

export default function VendorStatement() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [expandedReport, setExpandedReport] = useState(true);
  const [values, setValues] = useState({});
  const { allVendors } = useSelector((state) => state.VendorReducer);

  const formatDate = (date) =>
    date ? moment(date).format("YYYY-MM-DD") : null;

  useEffect(() => {
    if (Object.keys(values).length) {
      formik.setValues({
        from_date: new Date(values.from_date),
        to_date: new Date(values.to_date),
        id: values.id || [],
      });
      setExpandedReport(false);
      setExpanded(true);
    }
  }, [values]);

  useEffect(() => {
    dispatch(getVendors());
  }, []);

  const formik = useFormik({
    initialValues: {
      from_date: new Date(new Date().getFullYear(), 0, 1),
      to_date: new Date(),
      id: [],
    },
    validationSchema: Yup.object({
      from_date: Yup.string().required("From Date Required"),
      to_date: Yup.string().required("To Date Required"),
      vendor_id: Yup.array().min(1, "At least one vendor must be selected"),
    }),
    onSubmit: (values) => {
      setValues({
        ...values,
        from_date: formatDate(values.from_date),
        to_date: formatDate(values.to_date),
      });
    },
  });

  return (
    <div className="mx-4 rounded-lg bg-white">
      <form onSubmit={formik.handleSubmit}>
        <div className="card shadow-md rounded-lg p-4 mt-4">
          <Divider
            onClick={() => setExpandedReport(!expandedReport)}
            className="hover:cursor-pointer"
          >
            <span className="text-2xl font-bold p-font-style text-center text-primary mx-1">
              Vendor Statement Report
            </span>
          </Divider>
          <motion.div
            className="overflow-hidden"
            animate={{ height: expandedReport ? "fit-content" : 0 }}
          >
            <div className="px-4 gap-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <div className="flex flex-col gap-2 p-fluid">
                <label htmlFor="from_date">{t("from_date")}</label>
                <Calendar
                  id="from_date"
                  name="from_date"
                  value={formik.values.from_date}
                  onChange={(e) => formik.setFieldValue("from_date", e.value)}
                  onBlur={formik.handleBlur}
                  className="w-full calender p-primary-cld-btn"
                  showIcon
                />
                {formik.touched.from_date && formik.errors.from_date && (
                  <div className="p-error">{formik.errors.from_date}</div>
                )}
              </div>
              <div className="flex flex-col gap-2 p-fluid">
                <label htmlFor="to_date">{t("to_date")}</label>
                <Calendar
                  id="to_date"
                  name="to_date"
                  value={formik.values.to_date}
                  onChange={(e) => formik.setFieldValue("to_date", e.value)}
                  onBlur={formik.handleBlur}
                  className="w-full calender p-primary-cld-btn"
                  showIcon
                />
                {formik.touched.to_date && formik.errors.to_date && (
                  <div className="p-error">{formik.errors.to_date}</div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="vendor_id">{t("vendor_name")}</label>
                <MultiSelect
                  value={formik.values.vendor_id}
                  onChange={formik.handleChange}
                  options={allVendors}
                  optionLabel="name"
                  id="vendor_id"
                  name="vendor_id"
                  filter
                  placeholder="Select Vendors"
                  className="!w-full p-primary-input"
                />
                {formik.touched.vendor_id && formik.errors.vendor_id && (
                  <div className="p-error">{formik.errors.vendor_id}</div>
                )}
              </div>
            </div>
            <div className="mt-16 flex justify-end gap-4">
              <Button
                label={t("cancel")}
                icon="pi pi-times"
                className="p-red-btn"
                type="button"
                onClick={formik.resetForm}
              />
              <Button
                label={t("submit")}
                icon="pi pi-check"
                className="p-secondary-btn"
                disabled={!formik.isValid || !formik.values.vendor_id?.length}
              />
            </div>
          </motion.div>
        </div>
      </form>
      {expanded && (
        <div className="card mt-10">
          <div className="flex h-full">
            <button
              onClick={() => setExpandedReport(!expandedReport)}
              className="background-clr-primary text-white font-bold p-font-style py-2 px-4 rounded-l"
            >
              <i className="pi pi-angle-double-left"></i>
            </button>
            <div className="h-1 w-full background-clr-primary"></div>
            <button
              onClick={() => setExpandedReport(!expandedReport)}
              className="background-clr-primary text-white font-bold p-font-style py-2 px-4 rounded-r"
            >
              <i className="pi pi-angle-double-left"></i>
            </button>
          </div>
          <VendorSalesReportsTable values={values} />
        </div>
      )}
    </div>
  );
}
